import React, { useEffect, useState, useMemo, useRef } from "react";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { motion, AnimatePresence } from "framer-motion";
import {
  Typography,
  Box,
  Stack,
  Portal,
  Checkbox,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  IconButton,
} from "@mui/material";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import { fixGrammarAndSpell } from "../../../services/ai.service";
import Grid from "@mui/material/Grid2";
import { DatePicker as MuiDatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import AssigneePicker from "../../../components/AssigneePicker";
import AssigneeSelect from "../../../components/AssigneeSelect";
import { ToastError, ToastSuccess } from "../../../utils/Toaster/Toaster";
import { PRIORITY, TASK_STATUS, DEFAULT_PRIVATE_ASSIGNEES } from "../../../constant/constant";
import { createTodoTask, updateTodoTask } from "../../../services/todoTask.service";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { FileUploader } from "react-drag-drop-files";
import moment from "moment";
import { IoClose } from "react-icons/io5";
import { FiPaperclip, FiCheck, FiRefreshCw } from "react-icons/fi";

// Normalize any date-like value (dayjs / moment / Date / string) → native Date
const toNativeDate = (v) => {
  if (!v) return null;
  if (v instanceof Date) return v;
  if (typeof v.toDate === "function") return v.toDate(); // dayjs / moment
  if (typeof v.valueOf === "function") return new Date(v.valueOf()); // timestamp
  return new Date(v);
};

// ─── Design tokens ─────────────────────────────────────────────────────────────
const T = {
  primary: "#0D9488",
  primaryDark: "#0F766E",
  primaryLight: "#F0FDFA",
  primaryGlow: "rgba(13,148,136,0.14)",
  border: "#E2E8F0",
  borderFocus: "#0D9488",
  text: "#0F172A",
  textSub: "#475569",
  textMuted: "#94A3B8",
  surface: "#FFFFFF",
  bg: "#F8FAFC",
  error: "#EF4444",
  errorLight: "#FEF2F2",
};

// ─── Priority & Status Config ──────────────────────────────────────────────────
const PRIORITY_OPTIONS = [
  { label: "High", value: PRIORITY.HIGH, color: "#DC2626", bg: "#FEF2F2", border: "#FCA5A5" },
  { label: "Medium", value: PRIORITY.MEDIUM, color: "#D97706", bg: "#FFFBEB", border: "#FCD34D" },
  { label: "Low", value: PRIORITY.LOW, color: "#059669", bg: "#ECFDF5", border: "#6EE7B7" },
];

const CREATE_STATUS_OPTIONS = [
  { label: "To Do",       value: TASK_STATUS.TODO,       color: "#64748B", bg: "#F1F5F9" },
  { label: "In Progress", value: TASK_STATUS.INPROGRESS, color: "#0D9488", bg: "#F0FDFA" },
  { label: "Completed",   value: TASK_STATUS.COMPLETED,  color: "#16A34A", bg: "#F0FDF4" },
];
const EDIT_STATUS_OPTIONS = [
  ...CREATE_STATUS_OPTIONS,
  { label: "Not Assigned",    value: TASK_STATUS.NOTASSIGNED,     color: "#B45309", bg: "#FFFBEB" },
  { label: "Acknowledge",     value: TASK_STATUS.ACKNOWLEDGE,     color: "#2563EB", bg: "#EFF6FF" },
  { label: "Rework Required", value: TASK_STATUS.REWORK_REQUIRED, color: "#DC2626", bg: "#FEF2F2" },
  { label: "Hold",            value: TASK_STATUS.HOLD,            color: "#C2410C", bg: "#FFF7ED" },
  { label: "Duplicate",       value: TASK_STATUS.DUPLICATE,       color: "#64748B", bg: "#F8FAFC" },
];

const isValidDate = (v) => {
  if (!v) return false;
  const d = v instanceof Date ? v : new Date(v?.valueOf?.() ?? v);
  return !isNaN(d.getTime());
};

const buildValidationSchema = (requireAssignee) =>
  Yup.object().shape({
    title: Yup.string().trim().required("Title is required"),
    assignees: requireAssignee
      ? Yup.array().of(Yup.string()).min(1, "At least one assignee is required")
      : Yup.array().of(Yup.string()),
    priority: Yup.number().required("Priority is required"),
    actualDeadlineDate: Yup.mixed()
      .nullable()
      .required("Deadline is required")
      .test("valid-date", "Invalid deadline date", (v) => !v || isValidDate(v)),
  });

// ─── Animated field wrapper ─────────────────────────────────────────────────
const FieldBlock = ({ label, error, children, required }) => (
  <Box>
    <Typography
      sx={{
        fontSize: "11px",
        fontWeight: 700,
        color: error ? T.error : T.textMuted,
        textTransform: "uppercase",
        letterSpacing: "0.07em",
        mb: 0.8,
      }}
    >
      {label}
      {required && (
        <Box component="span" sx={{ color: T.error, ml: 0.3 }}>*</Box>
      )}
    </Typography>
    {children}
    <AnimatePresence>
      {error && (
        <motion.div
          initial={{ opacity: 0, y: -4, height: 0 }}
          animate={{ opacity: 1, y: 0, height: "auto" }}
          exit={{ opacity: 0, y: -4, height: 0 }}
          transition={{ duration: 0.18 }}
        >
          <Typography sx={{ fontSize: "11px", color: T.error, mt: 0.5, fontWeight: 500 }}>
            {error}
          </Typography>
        </motion.div>
      )}
    </AnimatePresence>
  </Box>
);

// ─── Animated text input ──────────────────────────────────────────────────────
const PremiumInput = ({ name, value, onChange, onFocus, onBlur, placeholder, focused }) => (
  <Box
    sx={{
      border: `1.5px solid ${focused ? T.borderFocus : T.border}`,
      borderRadius: "10px",
      bgcolor: focused ? T.primaryLight : T.surface,
      boxShadow: focused ? `0 0 0 3px ${T.primaryGlow}` : "none",
      transition: "all 0.2s ease",
      overflow: "hidden",
    }}
  >
    <Box
      component="input"
      name={name}
      value={value}
      onChange={onChange}
      onFocus={onFocus}
      onBlur={onBlur}
      placeholder={placeholder}
      sx={{
        width: "100%",
        border: "none",
        background: "transparent",
        outline: "none",
        px: 1.8,
        py: 1.2,
        fontSize: "14px",
        fontWeight: 500,
        color: T.text,
        fontFamily: "inherit",
        "&::placeholder": { color: T.textMuted },
        boxSizing: "border-box",
      }}
    />
  </Box>
);

// ─── Pill selector ────────────────────────────────────────────────────────────
const PillSelector = ({ options, value, onChange }) => (
  <Stack direction="row" spacing={1} flexWrap="wrap">
    {options.map((opt) => {
      const active = value === opt.value;
      return (
        <motion.div key={opt.value} whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }}>
          <Box
            component="button"
            type="button"
            onClick={() => onChange(opt.value)}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.7,
              px: 1.8,
              py: 0.8,
              border: `1.5px solid ${active ? opt.color : T.border}`,
              borderRadius: "8px",
              bgcolor: active ? opt.bg : T.surface,
              color: active ? opt.color : T.textSub,
              cursor: "pointer",
              fontFamily: "inherit",
              fontSize: "12px",
              fontWeight: 700,
              transition: "all 0.15s ease",
              "&:hover": { borderColor: opt.color, bgcolor: opt.bg, color: opt.color },
            }}
          >
            {active && <FiCheck size={11} />}
            {opt.label}
          </Box>
        </motion.div>
      );
    })}
  </Stack>
);

// ─── Main modal component ─────────────────────────────────────────────────────
const AddTodoTaskModal = ({ open, onClose, fetchTasks, mode = "add", initialData = null }) => {
  const [loading, setLoading] = useState(false);
  const [titleFocused, setTitleFocused] = useState(false);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const datePickerRef = useRef(null);
  // Chip labels for already-selected assignees, so they render before the
  // first user fetch resolves (and survive a server-side search).
  const [assigneePreloads, setAssigneePreloads] = useState(() =>
    (initialData?.assignees || []).map((a) => ({
      _id: String(a.user ?? a._id ?? a),
      label: a.name || a.userName || "…",
      file: typeof a.file === "string" ? a.file : a.file?.location || null,
    }))
  );
  const [autoSupportPreload, setAutoSupportPreload] = useState(null);
  const [autoAcknowledgedByPreload, setAutoAcknowledgedByPreload] = useState(null);
  const [allowDeadlineModification, setAllowDeadlineModification] = useState(false);
  const [privateAssignees, setPrivateAssignees] = useState(DEFAULT_PRIVATE_ASSIGNEES);
  const [deadlineDlg, setDeadlineDlg] = useState({ open: false, pendingDate: null });
  const [dlgReason, setDlgReason] = useState("");
  const [dlgReasonError, setDlgReasonError] = useState("");
  const [titleFixing, setTitleFixing] = useState(false);
  const [titleFixed, setTitleFixed] = useState(false);
  const [descFixing, setDescFixing] = useState(false);
  const [descFixed, setDescFixed] = useState(false);

  const stripHtml = (html) => {
    if (!html) return "";
    const div = document.createElement("div");
    div.innerHTML = html;
    return div.innerText || div.textContent || "";
  };

  const handleTitleFix = async (currentTitle, setFieldValue) => {
    if (titleFixing || !currentTitle?.trim()) return;
    setTitleFixing(true);
    try {
      const res = await fixGrammarAndSpell(currentTitle.trim());
      const corrected = res?.correctedText;
      if (corrected) {
        setFieldValue("title", corrected);
        setTitleFixed(true);
        setTimeout(() => setTitleFixed(false), 2000);
      }
    } catch (_) {}
    finally { setTitleFixing(false); }
  };

  const handleDescFix = async (currentDesc, setFieldValue) => {
    if (descFixing) return;
    const plain = stripHtml(currentDesc).trim();
    if (!plain) return;
    setDescFixing(true);
    try {
      const res = await fixGrammarAndSpell(plain);
      const corrected = res?.correctedText;
      if (corrected) {
        const lines = corrected.split("\n").filter((l) => l.trim());
        const html = lines.length > 1
          ? lines.map((l) => `<p>${l}</p>`).join("")
          : `<p>${corrected}</p>`;
        setFieldValue("description", html);
        setDescFixed(true);
        setTimeout(() => setDescFixed(false), 2000);
      }
    } catch (_) {}
    finally { setDescFixing(false); }
  };

  // Clear the auto-fill preloads whenever the modal opens fresh
  useEffect(() => {
    if (open) {
      setAutoSupportPreload(null);
      setAutoAcknowledgedByPreload(null);
    }
  }, [open]);

  // Hydrate the privacy flag on edit; a fresh "add" starts from the default.
  // `|| false` here would clobber that default the moment this effect runs with
  // initialData === null. `open` is in the deps so re-opening the add modal
  // resets the toggle rather than inheriting the previous session's choice.
  useEffect(() => {
    setPrivateAssignees(
      initialData ? !!initialData.privateAssignees : DEFAULT_PRIVATE_ASSIGNEES
    );
  }, [initialData, open]);

  // Sync allowDeadlineModification with initialData
  useEffect(() => {
    setAllowDeadlineModification(initialData?.allowDeadlineModification || false);
  }, [initialData]);

  // Close the calendar on any mousedown outside its container.
  // We use mousedown (not click) because the modal stops click propagation
  // via e.stopPropagation(), which would prevent MUI's internal ClickAwayListener
  // from ever firing. mousedown is NOT stopped, so it reliably reaches document.
  useEffect(() => {
    if (!datePickerOpen) return;
    const handleMouseDown = (e) => {
      if (datePickerRef.current && !datePickerRef.current.contains(e.target)) {
        setDatePickerOpen(false);
      }
    };
    document.addEventListener("mousedown", handleMouseDown);
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, [datePickerOpen]);

  const isAdmin = useMemo(() => {
    const isOverAllAdmin = localStorage.getItem("isOverAllAdmin");
    const isAdminFlag = localStorage.getItem("isAdmin");
    const role = localStorage.getItem("role");
    return isOverAllAdmin === "true" || isAdminFlag === "true" || role?.toUpperCase() === "FEEDBACK-ADMIN";
  }, []);

  const currentUserId = useMemo(() => localStorage.getItem("userId"), []);
  const isCreator = useMemo(() => (
    mode === "edit" && initialData?.assignedBy &&
    String(initialData.assignedBy) === String(currentUserId)
  ), [mode, initialData, currentUserId]);
  const canControlDeadline = isAdmin || isCreator;
  const isDeadlineEditable = mode === "add" || canControlDeadline || allowDeadlineModification;

  const hasDeadlineChanged = (original, current) => {
    if (!original && !current) return false;
    if (!original || !current) return true;
    const fmt = (d) => {
      const date = d instanceof Date ? d : new Date(d?.valueOf?.() ?? d);
      return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
    };
    return fmt(original) !== fmt(current);
  };

  const validationSchema = useMemo(() => buildValidationSchema(false), []);


  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const formData = new FormData();
      Object.keys(values).forEach((key) => {
        if (key === "files") {
          values.files.forEach((f) => formData.append("files", f));
        } else if (key === "assignees") {
          // ONE JSON string. Repeated appends are ambiguous by cardinality —
          // multer yields an array for 2+, a bare string for 1 and undefined
          // for 0 — and an intentional clear-to-empty is inexpressible.
          formData.append("assignees", JSON.stringify(values.assignees || []));
        } else if (key === "deadlineChangeReason") {
          // handled separately below
        } else if (values[key] !== null && values[key] !== undefined) {
          if (key === "actualDeadlineDate" && values[key]) {
            // Send as date-only string so MongoDB stores UTC midnight of the chosen day,
            // avoiding the UTC-offset shift that toISOString() causes for IST users.
            const d = values[key] instanceof Date ? values[key] : new Date(values[key]);
            const dateOnly = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
            formData.append(key, dateOnly);
          } else {
            formData.append(key, values[key]);
          }
        }
      });

      formData.append("allowDeadlineModification", String(allowDeadlineModification));
      formData.append("privateAssignees", String(privateAssignees));
      if (values.deadlineChangeReason?.trim()) {
        formData.append("deadlineChangeReason", values.deadlineChangeReason.trim());
      }

      let res;
      if (mode === "edit" && initialData?._id) {
        res = await updateTodoTask(initialData._id, formData);
      } else {
        res = await createTodoTask(formData);
      }

      if (res?.error === false) {
        ToastSuccess(`Task ${mode === "edit" ? "updated" : "created"} successfully`);
        fetchTasks();
        onClose();
      } else {
        ToastError(res?.message || "Operation failed");
      }
    } catch (error) {
      ToastError(error?.response?.data?.message || error?.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const initialValues = {
    title: initialData?.title || "",
    description: initialData?.description || "",
    assignees: Array.isArray(initialData?.assignees)
      ? initialData.assignees.map((a) => String(a.user ?? a._id ?? a))
      : initialData?.assignee
      ? [String(initialData.assignee)]
      : [],
    support: initialData?.support || "",
    acknowledgedBy: initialData?.acknowledgedBy || "",
    priority: typeof initialData?.priority === "number" ? initialData.priority : PRIORITY.MEDIUM,
    actualDeadlineDate: initialData?.actualDeadlineDate
      ? moment(initialData.actualDeadlineDate).toDate()
      : null,
    status: typeof initialData?.status === "number" ? initialData.status : TASK_STATUS.TODO,
    files: [],
    deadlineChangeReason: "",
    serialNo: initialData?.serialNo || "",
  };

  return (
    <AnimatePresence>
      {open && (
        <Portal>
          {/* Backdrop */}
          <motion.div
            key="backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.22 }}
            onClick={onClose}
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(15,23,42,0.45)",
              backdropFilter: "blur(4px)",
              zIndex: 1200,
            }}
          />

          {/* Centering wrapper — flexbox owns the position, Framer Motion only animates opacity/scale/y */}
          <div
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 1201,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              pointerEvents: "none",
            }}
          >
            <motion.div
              key="modal"
              initial={{ opacity: 0, scale: 0.93, y: 24 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.93, y: 20 }}
              transition={{ type: "spring", stiffness: 360, damping: 28, mass: 0.8 }}
              style={{
                pointerEvents: "auto",
                width: "min(680px, calc(100vw - 32px))",
                maxHeight: "calc(100vh - 48px)",
                overflowY: "auto",
                background: "#FFFFFF",
                borderRadius: "18px",
                boxShadow: "0 32px 80px -8px rgba(0,0,0,0.22), 0 8px 24px rgba(0,0,0,0.10)",
                border: "1px solid #E2E8F0",
              }}
              // inject keyframe for recurring icon spin
              {...(typeof document !== "undefined" && (() => {
                if (!document.getElementById("recur-spin-style")) {
                  const s = document.createElement("style");
                  s.id = "recur-spin-style";
                  s.textContent = "@keyframes recurSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }";
                  document.head.appendChild(s);
                }
                return {};
              })())}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal header */}
              <Box
                sx={{
                  px: 3,
                  py: 2.5,
                  borderBottom: `1px solid ${T.border}`,
                  background: `linear-gradient(135deg, ${T.primaryLight} 0%, #fff 100%)`,
                  position: "sticky",
                  top: 0,
                  zIndex: 1,
                  borderRadius: "18px 18px 0 0",
                }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: "10px",
                        background: `linear-gradient(135deg, ${T.primary}, ${T.primaryDark})`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        boxShadow: "0 4px 12px rgba(13,148,136,0.3)",
                      }}
                    >
                      <Box
                        component="span"
                        sx={{
                          width: 16,
                          height: 16,
                          borderRadius: "4px",
                          border: "2.5px solid #fff",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <Box
                          component="span"
                          sx={{
                            width: 6,
                            height: 6,
                            bgcolor: "#fff",
                            borderRadius: "1px",
                            display: "block",
                          }}
                        />
                      </Box>
                    </Box>
                    <Box>
                      <Typography
                        sx={{ fontWeight: 800, fontSize: "16px", color: T.text, lineHeight: 1.2 }}
                      >
                        {mode === "edit" ? "Edit Task" : "Create New Task"}
                      </Typography>
                      <Typography sx={{ fontSize: "12px", color: T.textMuted, fontWeight: 500 }}>
                        {mode === "edit" ? "Update task details" : "Fill in the details below"}
                      </Typography>
                    </Box>
                  </Stack>

                  <motion.button
                    whileHover={{ scale: 1.1, rotate: 90 }}
                    whileTap={{ scale: 0.9 }}
                    transition={{ duration: 0.2 }}
                    type="button"
                    onClick={onClose}
                    style={{
                      border: "none",
                      background: T.bg,
                      borderRadius: "8px",
                      width: 32,
                      height: 32,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      color: T.textSub,
                      flexShrink: 0,
                    }}
                  >
                    <IoClose size={17} />
                  </motion.button>
                </Stack>
              </Box>

              {/* Modal body */}
              <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
                enableReinitialize
              >
                {({ values, errors, touched, setFieldValue, isSubmitting }) => (
                  <Form>
                    <Box sx={{ px: 3, py: 3 }}>
                      <Stack spacing={2.8}>
                        {/* Title */}
                        <FieldBlock
                          label="Task Title"
                          required
                          error={Boolean(touched.title && errors.title)}
                        >
                          <Box sx={{ position: "relative" }}>
                            <Field name="title">
                              {({ field }) => (
                                <PremiumInput
                                  name={field.name}
                                  value={field.value}
                                  onChange={field.onChange}
                                  onFocus={() => setTitleFocused(true)}
                                  onBlur={(e) => { setTitleFocused(false); field.onBlur(e); }}
                                  placeholder="e.g. Implement authentication flow"
                                  focused={titleFocused}
                                />
                              )}
                            </Field>
                            <Tooltip title="Fix spelling & grammar (AI)">
                              <IconButton
                                size="small"
                                onClick={() => handleTitleFix(values.title, setFieldValue)}
                                disabled={titleFixing || !values.title?.trim()}
                                sx={{
                                  position: "absolute", right: 6, top: "50%",
                                  transform: "translateY(-50%)",
                                  width: 26, height: 26, borderRadius: "6px",
                                  color: titleFixed ? "#16A34A" : "#7C3AED",
                                  bgcolor: titleFixed ? "#F0FDF4" : "#FAF5FF",
                                  border: `1px solid ${titleFixed ? "#86EFAC" : "#DDD6FE"}`,
                                  transition: "all 0.2s",
                                  "&:hover": { bgcolor: "#EDE9FE", color: "#6D28D9" },
                                  "&.Mui-disabled": { opacity: 0.4 },
                                }}
                              >
                                {titleFixing
                                  ? <CircularProgress size={11} sx={{ color: "#7C3AED" }} />
                                  : <AutoFixHighIcon sx={{ fontSize: 13 }} />
                                }
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </FieldBlock>

                        {/* S.No */}
                        <FieldBlock label="S.No (Optional)">
                          <Field name="serialNo">
                            {({ field }) => (
                              <PremiumInput
                                name={field.name}
                                value={field.value}
                                onChange={field.onChange}
                                placeholder="e.g. 06-01 (auto-generated if blank)"
                                focused={false}
                              />
                            )}
                          </Field>
                        </FieldBlock>

                        {/* Row: Assignee + Support + Deadline — unified input panel */}
                        <Box
                          sx={{
                            border: `1.5px solid ${T.border}`,
                            borderRadius: "12px",
                            // overflow must stay visible so the controlled calendar (disablePortal)
                            // can render outside the panel bounds without being clipped
                            overflow: "visible",
                            bgcolor: T.surface,
                            transition: "border-color 0.2s",
                            "&:focus-within": { borderColor: T.borderFocus },
                          }}
                        >
                          <Grid container>
                            {/* Assignees — full width: chips wrap, unlike the
                                single-value Support / Acknowledged By fields. */}
                            <Grid size={{ xs: 12, sm: 12 }}
                              sx={{ borderBottom: `1px solid ${T.border}` }}
                            >
                              <Box sx={{ px: 1.6, pt: 1.1, pb: 0.3, display: "flex", alignItems: "center", gap: 0.5 }}>
                                <Typography sx={{ fontSize: "10px", fontWeight: 700, color: T.textMuted, textTransform: "uppercase", letterSpacing: "0.08em" }}>
                                  Assignee
                                </Typography>
                                {(mode === "add" || canControlDeadline) && (
                                  <Tooltip
                                    title={privateAssignees
                                      ? "Uncheck to let assignees see each other on this task"
                                      : "Each assignee will only see their own line — no other names, avatars or counts"}
                                    placement="top"
                                  >
                                    <Box
                                      sx={{ display: "flex", alignItems: "center", ml: "auto", gap: 0.3, cursor: "pointer" }}
                                      onClick={() => setPrivateAssignees((v) => !v)}
                                    >
                                      <Checkbox
                                        size="small"
                                        checked={privateAssignees}
                                        onChange={(e) => setPrivateAssignees(e.target.checked)}
                                        sx={{ p: 0.2, color: T.textMuted, "&.Mui-checked": { color: T.primary } }}
                                      />
                                      <Typography sx={{ fontSize: "10px", fontWeight: 600, color: privateAssignees ? T.primary : T.textMuted, userSelect: "none", whiteSpace: "nowrap" }}>
                                        Keep assignee info private
                                      </Typography>
                                    </Box>
                                  </Tooltip>
                                )}
                              </Box>
                              <Box sx={{
                                px: 0.5, pb: 0.6,
                                "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                                "& .MuiInputBase-root": { height: "36px", bgcolor: "transparent" },
                                "& .MuiInputBase-input": { py: "0 !important", fontSize: "13px" },
                              }}>
                                <AssigneeSelect
                                  value={values.assignees}
                                  preloaded={assigneePreloads}
                                  onChange={(ids, options) => {
                                    const wasEmpty = (values.assignees || []).length === 0;
                                    setFieldValue("assignees", ids);
                                    setAssigneePreloads(options);
                                    // Auto-fill fires ONLY on the 0 -> 1 transition and
                                    // never overwrites a value the user already chose.
                                    // Adding a 2nd..Nth assignee must not silently
                                    // rewrite these single-valued fields.
                                    const first = options[0];
                                    if (wasEmpty && first) {
                                      if (!values.support) {
                                        setFieldValue("support", first._id);
                                        setAutoSupportPreload(first);
                                      }
                                      if (!values.acknowledgedBy) {
                                        setFieldValue("acknowledgedBy", first._id);
                                        setAutoAcknowledgedByPreload(first);
                                      }
                                    }
                                  }}
                                  placeholder="Search members…"
                                  emptyLabel="Add assignees"
                                  error={touched.assignees && Boolean(errors.assignees)}
                                  roles="HR,PROJECT MANAGER"
                                />
                              </Box>
                            </Grid>

                            {/* Support */}
                            <Grid size={{ xs: 12, sm: 6 }}
                              sx={{
                                borderRight: { sm: `1px solid ${T.border}` },
                                borderBottom: { xs: `1px solid ${T.border}`, sm: "none" },
                              }}
                            >
                              <Box sx={{ px: 1.6, pt: 1.1, pb: 0.3 }}>
                                <Typography sx={{ fontSize: "10px", fontWeight: 700, color: T.textMuted, textTransform: "uppercase", letterSpacing: "0.08em" }}>
                                  Support
                                </Typography>
                              </Box>
                              <Box sx={{
                                px: 0.5, pb: 0.6,
                                "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                                "& .MuiInputBase-root": { height: "36px", bgcolor: "transparent" },
                                "& .MuiInputBase-input": { py: "0 !important", fontSize: "13px" },
                              }}>
                                <AssigneePicker
                                  value={values.support}
                                  onChange={(id) => setFieldValue("support", id || "")}
                                  placeholder="Select support…"
                                  preloadedOption={autoSupportPreload}
                                  roles="HR,PROJECT MANAGER"
                                />
                              </Box>
                            </Grid>

                            {/* Acknowledged By */}
                            <Grid size={{ xs: 12, sm: 6 }}
                              sx={{
                                borderRight: { sm: `1px solid ${T.border}` },
                                borderBottom: { xs: `1px solid ${T.border}`, sm: "none" },
                              }}
                            >
                              <Box sx={{ px: 1.6, pt: 1.1, pb: 0.3 }}>
                                <Typography sx={{ fontSize: "10px", fontWeight: 700, color: T.textMuted, textTransform: "uppercase", letterSpacing: "0.08em" }}>
                                  Acknowledged By
                                </Typography>
                              </Box>
                              <Box sx={{
                                px: 0.5, pb: 0.6,
                                "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                                "& .MuiInputBase-root": { height: "36px", bgcolor: "transparent" },
                                "& .MuiInputBase-input": { py: "0 !important", fontSize: "13px" },
                              }}>
                                <AssigneePicker
                                  value={values.acknowledgedBy}
                                  onChange={(id) => setFieldValue("acknowledgedBy", id || "")}
                                  placeholder="Select person…"
                                  preloadedOption={autoAcknowledgedByPreload}
                                  roles="HR,PROJECT MANAGER"
                                />
                              </Box>
                            </Grid>

                            {/* Deadline */}
                            <Grid size={{ xs: 12 }}
                              sx={{ borderTop: `1px solid ${T.border}` }}
                            >
                              <Box sx={{ px: 1.6, pt: 1.1, pb: 0.3, display: "flex", alignItems: "center", gap: 0.5 }}>
                                <Typography sx={{ fontSize: "10px", fontWeight: 700, color: touched.actualDeadlineDate && errors.actualDeadlineDate ? T.error : T.textMuted, textTransform: "uppercase", letterSpacing: "0.08em" }}>
                                  Deadline
                                </Typography>
                                <Typography component="span" sx={{ fontSize: "10px", color: T.error, lineHeight: 1 }}>*</Typography>
                                {(mode === "add" || canControlDeadline) && (
                                  <Tooltip
                                    title={allowDeadlineModification ? "Uncheck to lock deadline for other participants" : "Check to allow other participants to modify the deadline"}
                                    placement="top"
                                  >
                                    <Box
                                      sx={{ display: "flex", alignItems: "center", ml: "auto", gap: 0.3, cursor: "pointer" }}
                                      onClick={() => setAllowDeadlineModification((v) => !v)}
                                    >
                                      <Checkbox
                                        size="small"
                                        checked={allowDeadlineModification}
                                        onChange={(e) => setAllowDeadlineModification(e.target.checked)}
                                        sx={{ p: 0.2, color: T.textMuted, "&.Mui-checked": { color: T.primary } }}
                                      />
                                      <Typography sx={{ fontSize: "10px", fontWeight: 600, color: allowDeadlineModification ? T.primary : T.textMuted, userSelect: "none" }}>
                                        Allow Modification
                                      </Typography>
                                    </Box>
                                  </Tooltip>
                                )}
                              </Box>
                              <Box
                                ref={datePickerRef}
                                sx={{
                                  px: 0.5, pb: 0.6,
                                  "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                                  "& .MuiInputBase-root": { height: "36px", bgcolor: "transparent" },
                                  "& .MuiInputBase-input": { py: "0 !important", fontSize: "13px", cursor: isDeadlineEditable ? undefined : "not-allowed" },
                                  opacity: isDeadlineEditable ? 1 : 0.6,
                                }}
                              >
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                  <MuiDatePicker
                                    open={isDeadlineEditable ? datePickerOpen : false}
                                    onOpen={() => { if (isDeadlineEditable) setDatePickerOpen(true); }}
                                    onClose={() => setDatePickerOpen(false)}
                                    value={values.actualDeadlineDate ? dayjs(values.actualDeadlineDate) : null}
                                    onChange={(v) => {
                                      if (!isDeadlineEditable) return;
                                      setDatePickerOpen(false);
                                      if (!v) return;
                                      if (mode === "edit") {
                                        setDeadlineDlg({ open: true, pendingDate: v.toDate() });
                                        setDlgReason("");
                                        setDlgReasonError("");
                                      } else {
                                        setFieldValue("actualDeadlineDate", v.toDate());
                                      }
                                    }}
                                    readOnly={!isDeadlineEditable}
                                    disablePast={mode === "add"}
                                    format="DD/MM/YYYY"
                                    slotProps={{
                                      textField: {
                                        size: "small",
                                        fullWidth: true,
                                        placeholder: "Pick a date…",
                                        onClick: () => { if (isDeadlineEditable) setDatePickerOpen(true); },
                                        onKeyDown: (e) => { if (isDeadlineEditable && (e.key === "Enter" || e.key === " ")) setDatePickerOpen(true); },
                                      },
                                      popper: {
                                        disablePortal: true,
                                        placement: "bottom-end",
                                        sx: { zIndex: 1300 },
                                      },
                                    }}
                                  />
                                </LocalizationProvider>
                              </Box>
                              {touched.actualDeadlineDate && errors.actualDeadlineDate && (
                                <Typography sx={{ px: 1.6, pb: 0.8, fontSize: "11px", color: T.error, fontWeight: 500 }}>
                                  {errors.actualDeadlineDate}
                                </Typography>
                              )}
                            </Grid>
                          </Grid>
                        </Box>

                        {/* Priority */}
                        <FieldBlock label="Priority" error={touched.priority && errors.priority}>
                          <PillSelector
                            options={PRIORITY_OPTIONS}
                            value={values.priority}
                            onChange={(v) => setFieldValue("priority", v)}
                          />
                        </FieldBlock>

                        {/* Status */}
                        <FieldBlock label="Status">
                          <PillSelector
                            options={mode === "add" ? CREATE_STATUS_OPTIONS : EDIT_STATUS_OPTIONS}
                            value={values.status}
                            onChange={(v) => setFieldValue("status", v)}
                          />
                        </FieldBlock>

                        {/* Description */}
                        <FieldBlock label="Description">
                          <Box
                            sx={{
                              borderRadius: "10px",
                              overflow: "hidden",
                              border: `1.5px solid ${T.border}`,
                              transition: "border-color 0.2s",
                              "&:focus-within": { borderColor: T.borderFocus, boxShadow: `0 0 0 3px ${T.primaryGlow}` },
                              "& .quill": {
                                "& .ql-toolbar": {
                                  border: "none",
                                  borderBottom: `1px solid ${T.border}`,
                                  bgcolor: T.bg,
                                  padding: "8px 12px",
                                },
                                "& .ql-container": {
                                  border: "none",
                                  minHeight: "110px",
                                  fontSize: "14px",
                                },
                                "& .ql-editor": {
                                  minHeight: "110px",
                                  fontSize: "14px",
                                  color: T.text,
                                },
                                "& .ql-editor.ql-blank::before": {
                                  color: T.textMuted,
                                  fontStyle: "normal",
                                },
                              },
                            }}
                          >
                            <Field name="description">
                              {({ field, form }) => (
                                <ReactQuill
                                  value={field.value || ""}
                                  onChange={(v) => form.setFieldValue("description", v)}
                                  placeholder="Add more context, acceptance criteria, or links…"
                                  modules={{
                                    toolbar: [
                                      ["bold", "italic", "underline"],
                                      [{ list: "ordered" }, { list: "bullet" }],
                                      ["link", "clean"],
                                    ],
                                  }}
                                />
                              )}
                            </Field>
                          </Box>
                          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 0.8 }}>
                            <Tooltip title="Fix spelling & grammar (AI)">
                              <Button
                                size="small"
                                onClick={() => handleDescFix(values.description, setFieldValue)}
                                disabled={descFixing || !stripHtml(values.description).trim()}
                                startIcon={
                                  descFixing
                                    ? <CircularProgress size={11} sx={{ color: "#7C3AED" }} />
                                    : <AutoFixHighIcon sx={{ fontSize: "13px !important" }} />
                                }
                                sx={{
                                  textTransform: "none",
                                  fontSize: "12px",
                                  fontWeight: 600,
                                  px: 1.6,
                                  py: 0.5,
                                  borderRadius: "8px",
                                  color: descFixed ? "#16A34A" : "#7C3AED",
                                  bgcolor: descFixed ? "#F0FDF4" : "#FAF5FF",
                                  border: `1.5px solid ${descFixed ? "#86EFAC" : "#DDD6FE"}`,
                                  transition: "all 0.2s",
                                  "&:hover": { bgcolor: "#EDE9FE", color: "#6D28D9", border: "1.5px solid #C4B5FD" },
                                  "&.Mui-disabled": { opacity: 0.4 },
                                }}
                              >
                                {descFixing ? "Fixing…" : descFixed ? "Fixed!" : "Fix spelling & grammar"}
                              </Button>
                            </Tooltip>
                          </Box>
                        </FieldBlock>

                        {/* Attachments */}
                        <FieldBlock label="Attachments">
                          <Box
                            sx={{
                              "& label": {
                                borderRadius: "10px !important",
                                border: `1.5px dashed ${T.border} !important`,
                                background: `${T.bg} !important`,
                                transition: "all 0.2s !important",
                                "&:hover": {
                                  borderColor: `${T.primary} !important`,
                                  background: `${T.primaryLight} !important`,
                                },
                              },
                            }}
                          >
                            <FileUploader
                              handleChange={(files) => setFieldValue("files", Array.from(files))}
                              name="file"
                              types={["JPG", "PNG", "GIF", "PDF", "DOCX", "XLSX"]}
                              multiple={true}
                            />
                          </Box>
                          <AnimatePresence>
                            {values.files.length > 0 && (
                              <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: "auto" }}
                                exit={{ opacity: 0, height: 0 }}
                                transition={{ duration: 0.2 }}
                              >
                                <Stack
                                  direction="row"
                                  flexWrap="wrap"
                                  spacing={1}
                                  sx={{ mt: 1.5, pt: 1.5, borderTop: `1px solid ${T.border}` }}
                                >
                                  {values.files.map((file, i) => (
                                    <motion.div
                                      key={i}
                                      initial={{ opacity: 0, scale: 0.85 }}
                                      animate={{ opacity: 1, scale: 1 }}
                                      exit={{ opacity: 0, scale: 0.75 }}
                                      transition={{ delay: i * 0.05 }}
                                    >
                                      <Box
                                        sx={{
                                          display: "inline-flex",
                                          alignItems: "center",
                                          gap: 0.5,
                                          pl: 1.1,
                                          pr: 0.5,
                                          py: 0.5,
                                          borderRadius: "6px",
                                          bgcolor: T.bg,
                                          border: `1px solid ${T.border}`,
                                          fontSize: "11px",
                                          fontWeight: 600,
                                          color: T.textSub,
                                          maxWidth: 180,
                                          transition: "border-color 0.15s",
                                          "&:hover": { borderColor: "#FCA5A5" },
                                        }}
                                      >
                                        <FiPaperclip size={10} style={{ flexShrink: 0 }} />
                                        <Typography
                                          sx={{
                                            fontSize: "11px", fontWeight: 600,
                                            color: T.textSub, maxWidth: 120,
                                            overflow: "hidden", textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                          }}
                                        >
                                          {file.name}
                                        </Typography>
                                        <Box
                                          component="button"
                                          type="button"
                                          onClick={() =>
                                            setFieldValue(
                                              "files",
                                              values.files.filter((_, idx) => idx !== i)
                                            )
                                          }
                                          sx={{
                                            display: "flex", alignItems: "center",
                                            justifyContent: "center",
                                            width: 16, height: 16,
                                            borderRadius: "50%",
                                            border: "none",
                                            bgcolor: "transparent",
                                            color: T.textMuted,
                                            cursor: "pointer", p: 0, flexShrink: 0,
                                            transition: "all 0.15s",
                                            "&:hover": { bgcolor: "#FEE2E2", color: "#DC2626" },
                                          }}
                                        >
                                          <IoClose size={11} />
                                        </Box>
                                      </Box>
                                    </motion.div>
                                  ))}
                                </Stack>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </FieldBlock>
                      </Stack>
                    </Box>

                    {/* Modal footer */}
                    <Box
                      sx={{
                        px: 3,
                        py: 2.5,
                        borderTop: `1px solid ${T.border}`,
                        bgcolor: T.bg,
                        borderRadius: "0 0 18px 18px",
                        position: "sticky",
                        bottom: 0,
                      }}
                    >
                      <Stack direction="row" justifyContent="flex-end" spacing={1.5}>
                        <motion.button
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          type="button"
                          onClick={onClose}
                          style={{
                            padding: "9px 20px",
                            border: `1.5px solid ${T.border}`,
                            borderRadius: "10px",
                            background: T.surface,
                            color: T.textSub,
                            cursor: "pointer",
                            fontFamily: "inherit",
                            fontSize: "13px",
                            fontWeight: 600,
                            transition: "all 0.15s",
                          }}
                        >
                          Cancel
                        </motion.button>
                        <motion.button
                          whileHover={{ scale: 1.02, boxShadow: "0 6px 18px rgba(13,148,136,0.35)" }}
                          whileTap={{ scale: 0.98 }}
                          type="submit"
                          disabled={isSubmitting || loading}
                          style={{
                            padding: "9px 24px",
                            border: "none",
                            borderRadius: "10px",
                            background:
                              isSubmitting || loading
                                ? "#94A3B8"
                                : `linear-gradient(135deg, ${T.primary}, ${T.primaryDark})`,
                            color: "#fff",
                            cursor: isSubmitting || loading ? "not-allowed" : "pointer",
                            fontFamily: "inherit",
                            fontSize: "13px",
                            fontWeight: 700,
                            boxShadow: "0 2px 10px rgba(13,148,136,0.28)",
                            transition: "all 0.2s",
                            display: "flex",
                            alignItems: "center",
                            gap: 7,
                          }}
                        >
                          {loading ? (
                            <>
                              <Box
                                component="span"
                                sx={{
                                  width: 13,
                                  height: 13,
                                  border: "2px solid rgba(255,255,255,0.4)",
                                  borderTopColor: "#fff",
                                  borderRadius: "50%",
                                  animation: "spin 0.7s linear infinite",
                                  display: "inline-block",
                                  "@keyframes spin": { to: { transform: "rotate(360deg)" } },
                                }}
                              />
                              Processing…
                            </>
                          ) : mode === "edit" ? (
                            "Update Task"
                          ) : (
                            "Create Task"
                          )}
                        </motion.button>
                      </Stack>
                    </Box>

                    {/* Deadline change dialog */}
                    <Dialog
                      open={deadlineDlg.open}
                      onClose={() => { setDeadlineDlg({ open: false, pendingDate: null }); setDlgReason(""); setDlgReasonError(""); }}
                      maxWidth="xs"
                      fullWidth
                      PaperProps={{ sx: { borderRadius: "14px" } }}
                    >
                      <DialogTitle sx={{ fontSize: "15px", fontWeight: 700, pb: 1 }}>
                        Change Deadline
                      </DialogTitle>
                      <DialogContent>
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2, px: 1.5, py: 1, borderRadius: "8px", bgcolor: T.bg, border: `1px solid ${T.border}` }}>
                          <Typography sx={{ fontSize: "12.5px", color: T.textMuted }}>
                            {initialData?.actualDeadlineDate ? dayjs(initialData.actualDeadlineDate).format("D MMM YYYY") : "Not set"}
                          </Typography>
                          <Typography sx={{ fontSize: "12px", color: T.textMuted }}>→</Typography>
                          <Typography sx={{ fontSize: "12.5px", fontWeight: 700, color: T.primary }}>
                            {deadlineDlg.pendingDate ? dayjs(deadlineDlg.pendingDate).format("D MMM YYYY") : "—"}
                          </Typography>
                        </Stack>
                        <Typography sx={{ fontSize: "11px", fontWeight: 700, color: dlgReasonError ? T.error : T.textMuted, textTransform: "uppercase", letterSpacing: "0.07em", mb: 0.8 }}>
                          Reason for Change
                          {!canControlDeadline
                            ? <Box component="span" sx={{ color: T.error, ml: 0.3 }}>*</Box>
                            : <Box component="span" sx={{ fontSize: "10px", fontWeight: 500, color: T.textMuted, ml: 0.5, textTransform: "none", letterSpacing: 0 }}>(Optional)</Box>
                          }
                        </Typography>
                        <Box
                          component="textarea"
                          value={dlgReason}
                          onChange={(e) => { setDlgReason(e.target.value); if (e.target.value.trim()) setDlgReasonError(""); }}
                          placeholder="e.g. Client requested an extension…"
                          rows={3}
                          sx={{
                            width: "100%", boxSizing: "border-box",
                            border: `1.5px solid ${dlgReasonError ? T.error : T.border}`,
                            borderRadius: "8px", padding: "10px 12px",
                            fontSize: "13px", fontFamily: "inherit",
                            color: T.text, background: T.bg,
                            outline: "none", resize: "vertical", display: "block",
                            transition: "border-color 0.2s",
                            "&:focus": { borderColor: dlgReasonError ? T.error : T.borderFocus, boxShadow: `0 0 0 3px ${T.primaryGlow}` },
                            "&::placeholder": { color: T.textMuted },
                          }}
                        />
                        {dlgReasonError && (
                          <Typography sx={{ fontSize: "11px", color: T.error, mt: 0.5, fontWeight: 500 }}>{dlgReasonError}</Typography>
                        )}
                      </DialogContent>
                      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
                        <Button
                          onClick={() => { setDeadlineDlg({ open: false, pendingDate: null }); setDlgReason(""); setDlgReasonError(""); }}
                          sx={{ color: T.textSub, textTransform: "none", fontWeight: 600, borderRadius: "8px" }}
                        >
                          Cancel
                        </Button>
                        <Button
                          variant="contained"
                          onClick={() => {
                            if (!canControlDeadline && !dlgReason.trim()) { setDlgReasonError("Reason is required"); return; }
                            setFieldValue("actualDeadlineDate", deadlineDlg.pendingDate);
                            setFieldValue("deadlineChangeReason", dlgReason.trim());
                            setDeadlineDlg({ open: false, pendingDate: null });
                            setDlgReason("");
                            setDlgReasonError("");
                          }}
                          sx={{ textTransform: "none", fontWeight: 700, bgcolor: T.primary, "&:hover": { bgcolor: T.primaryDark }, borderRadius: "8px" }}
                        >
                          Confirm
                        </Button>
                      </DialogActions>
                    </Dialog>
                  </Form>
                )}
              </Formik>
            </motion.div>
          </div>
        </Portal>
      )}
    </AnimatePresence>
  );
};

export default AddTodoTaskModal;
